import * as R from 'remeda';
import { BREADCRUMB_SEPARATOR } from '@/shared/lib/format/separators';
import type { CascaderOption } from '../helpers/types';

/**
 * パスから選択されたオプションを取得
 */
export const getSelectedOptions = (path: string[], options: CascaderOption[]): CascaderOption[] => {
  const result: CascaderOption[] = [];
  let currentOptions = options;

  for (const val of path) {
    const option = currentOptions.find((opt) => opt.value === val);
    if (!option) break;
    result.push(option);
    currentOptions = option.children || [];
  }

  return result;
};

/**
 * パスが選択されているかチェック
 */
export const isPathSelected = (path: string[], selectedPaths: string[][]): boolean =>
  selectedPaths.some((selectedPath) => R.isDeepEqual(selectedPath, path));

/**
 * 子孫ノードをすべて取得（自分自身と全ての子孫を含む）
 */
export const getAllDescendantPaths = (path: string[], option: CascaderOption): string[][] => {
  const currentPath = [...path, option.value];
  return [
    currentPath,
    ...R.flatMap(option.children ?? [], (child) => getAllDescendantPaths(currentPath, child)),
  ];
};

/**
 * カラムごとのオプション取得
 */
export const getColumnOptions = (
  level: number,
  activePath: string[],
  options: CascaderOption[],
): CascaderOption[] => {
  if (level === 0) return options;

  let currentOptions = options;
  for (let i = 0; i < level; i++) {
    const parentValue = activePath[i];
    const parent = currentOptions.find((opt) => opt.value === parentValue);
    if (!parent?.children) return [];
    currentOptions = parent.children;
  }

  return currentOptions;
};

/**
 * パスが他のパスの祖先（プレフィックス）かどうかを判定
 */
const isAncestorOf = (ancestor: string[], descendant: string[]): boolean =>
  ancestor.length < descendant.length &&
  R.isDeepEqual(descendant.slice(0, ancestor.length), ancestor);

/**
 * Cascaderの値（パス配列）からリーフ値のみを抽出する
 *
 * Cascaderは複数選択モードで祖先パスも含めて値を保持するため、
 * API送信時などにリーフ値のみが必要な場合にこの関数を使用する。
 *
 * @example
 * // onlyLeafSelectable=true の場合（末端のみ選択可能）
 * // 祖先パスを除外してリーフパスの末尾値のみを抽出
 * extractLeafValues([['2024'], ['2024', 'uuid-1'], ['2023'], ['2023', 'uuid-2']], true, true)
 * // => ['uuid-1', 'uuid-2']
 *
 * @example
 * // onlyLeafSelectable=false の場合（全ノード選択可能）
 * // すべてのパスの末尾値を抽出
 * extractLeafValues([['2024'], ['2024', 'uuid-1']], true, false)
 * // => ['2024', 'uuid-1']
 *
 * @example
 * // 単一選択の場合
 * extractLeafValues(['2024', 'uuid-1'], false)
 * // => 'uuid-1'
 *
 * @param value - Cascaderの値（単一選択: string[], 複数選択: string[][]）
 * @param multiple - 複数選択モードかどうか
 * @param onlyLeafSelectable - 末端ノードのみ選択可能かどうか（デフォルト: true）
 * @returns リーフ値の配列（複数選択）または単一値（単一選択）
 */
export const extractLeafValues = (
  value: string[] | string[][],
  multiple: boolean,
  onlyLeafSelectable = true,
): string[] | string => {
  if (!multiple) {
    return R.last(value as string[]) ?? '';
  }

  const paths = value as string[][];
  const targetPaths = onlyLeafSelectable
    ? paths.filter((path) => !paths.some((otherPath) => isAncestorOf(path, otherPath)))
    : paths;

  return R.pipe(
    targetPaths,
    R.map((path) => R.last(path)),
    R.filter(R.isNonNullish),
  );
};

/**
 * Cascader パスをオプションツリーからラベル文字列に解決する
 *
 * フィルタータグ等の表示用。パスの各ノードのラベルを BREADCRUMB_SEPARATOR で結合する。
 */
export const resolveCascaderPathLabel = (path: string[], options: CascaderOption[]): string => {
  const labels: string[] = [];
  let currentOptions = options;
  for (const value of path) {
    const found = currentOptions.find((opt) => opt.value === value);
    if (!found) break;
    labels.push(found.label);
    currentOptions = found.children ?? [];
  }
  return labels.join(BREADCRUMB_SEPARATOR);
};
